// MagicLinkGate — session gate for magic link authenticated tenants.
//
// Replaces ProtectedRoute + AuthProvider for tenants using magic link auth.
// The rq_session cookie is HttpOnly and carries the credential — the browser
// sends it automatically on every request to the backend. This component only
// checks for cookie *presence* as a lightweight indicator of whether the user
// has previously authenticated. Actual session validity is confirmed server-side.
//
// Flow:
//   No cookie detected → render MagicLinkLoginPage (email form)
//   Cookie present     → render children (RootLayout + Outlet via AuthenticatedMagicLinkShell)
//   Any 401 received   → clear cookie assumption, show login page with ?error=expired hint
//
// The ApiClient created here passes no Authorization header — the session cookie
// is sent automatically by the browser (HttpOnly, SameSite=Lax).

import { useState, useCallback, useMemo } from 'react'
import { MagicLinkLoginPage } from '@/pages/MagicLinkLoginPage'
import { createApiClient, ApiClientContext, ApiRequestError, type ApiClient } from '@/lib/api/client'
import { useFetchMe, UserMeProvider } from '@/lib/hooks/use-current-user'
import { RootLayout } from '@/components/layout/RootLayout'
import { useBranding } from '@/lib/hooks/use-branding'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Check for the rq_session_hint cookie.
 * rq_session (the actual credential) is HttpOnly and invisible to JavaScript.
 * rq_session_hint is a non-HttpOnly companion set by the backend at the same
 * time as rq_session — it carries no credential, only indicates that a session
 * was established. Actual validity is always confirmed server-side on each
 * API request; this check only gates the initial render.
 */
function hasSessionCookie(): boolean {
  return document.cookie.split(';').some((c) => c.trim().startsWith('rq_session_hint='))
}

// ---------------------------------------------------------------------------
// Intercepting ApiClient wrapper
// ---------------------------------------------------------------------------

/**
 * Wrap an ApiClient so that any 401 response triggers onUnauthorized.
 * This allows MagicLinkGate to react to session expiry without each individual
 * component needing to handle it.
 */
function createInterceptingClient(
  inner: ApiClient,
  onUnauthorized: () => void,
): ApiClient {
  async function intercept<T>(fn: () => Promise<T>): Promise<T> {
    try {
      return await fn()
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 401) {
        onUnauthorized()
      }
      throw err
    }
  }

  return {
    get: <T,>(path: string) => intercept(() => inner.get<T>(path)),
    post: <T,>(path: string, body?: unknown) => intercept(() => inner.post<T>(path, body)),
    put: <T,>(path: string, body?: unknown) => intercept(() => inner.put<T>(path, body)),
    del: (path: string) => intercept(() => inner.del(path)),
    fetch: (path, options) => intercept(() => inner.fetch(path, options)),
  }
}

// ---------------------------------------------------------------------------
// Main gate component
// ---------------------------------------------------------------------------

interface MagicLinkGateProps {
  slug: string
  tenantId: string
}

export function MagicLinkGate({ slug, tenantId }: MagicLinkGateProps) {
  // Cookie presence is the initial hint. Once a 401 arrives we force the
  // login page regardless of cookie state.
  const [sessionExpired, setSessionExpired] = useState(false)
  const [hasCookie, setHasCookie] = useState(() => hasSessionCookie())

  const handleUnauthorized = useCallback(() => {
    setSessionExpired(true)
    setHasCookie(false)
  }, [])

  // No cookie or session expired → show login page
  if (!hasCookie || sessionExpired) {
    return (
      <MagicLinkLoginPage
        slug={slug}
        initialError={sessionExpired ? 'expired' : null}
      />
    )
  }

  return (
    <AuthenticatedMagicLinkShell
      slug={slug}
      tenantId={tenantId}
      onUnauthorized={handleUnauthorized}
    />
  )
}

// ---------------------------------------------------------------------------
// Authenticated shell — rendered when cookie is present
// ---------------------------------------------------------------------------

interface AuthenticatedMagicLinkShellProps {
  slug: string
  tenantId: string
  onUnauthorized: () => void
}

function AuthenticatedMagicLinkShell({
  slug,
  tenantId,
  onUnauthorized,
}: AuthenticatedMagicLinkShellProps) {
  // ApiClient with no token getter — cookie is sent automatically by browser.
  // Wrapped with interceptor so 401s bubble up to MagicLinkGate.
  const apiClient = useMemo(() => {
    const base = createApiClient(tenantId, () => undefined)
    return createInterceptingClient(base, onUnauthorized)
  }, [tenantId, onUnauthorized])

  return (
    <ApiClientContext.Provider value={apiClient}>
      <MagicLinkBrandedLayout slug={slug} tenantId={tenantId} />
    </ApiClientContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Branded layout — mirrors BrandedLayout in TenantShell
// ---------------------------------------------------------------------------

interface MagicLinkBrandedLayoutProps {
  slug: string
  tenantId: string
}

function MagicLinkBrandedLayout({ slug, tenantId }: MagicLinkBrandedLayoutProps) {
  const branding = useBranding()
  const { data: me, isLoading: meLoading } = useFetchMe()

  const logoUrl = branding?.hasLogo ? `/api/v1/tenants/${tenantId}/branding/logo` : undefined

  if (meLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <UserMeProvider value={me ?? null}>
      <RootLayout slug={slug} logoUrl={logoUrl} />
    </UserMeProvider>
  )
}
